import React, { useState, useEffect, useCallback, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Link from "@material-ui/core/Link";
import AddIcon from "@material-ui/icons/Add";
import DashboardIcon from "@material-ui/icons/Dashboard";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import CreateTrelloCardModal from "../CreateTrelloCardModal";

const useStyles = makeStyles(() => ({
  header: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 8
  },
  cardRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "4px 0"
  },
  actions: { display: "flex", gap: 8, marginTop: 8 }
}));

const STATUS_COLORS = {
  open: "#ed6c02",
  in_progress: "#0288d1",
  done: "#2e7d32"
};

/**
 * Lista os chamados (cards Trello) vinculados ao ticket e permite criar novo.
 * Espelha o padrão do ImportantNotesWidget: mora no painel lateral do ticket.
 */
const TrelloCardsWidget = ({ ticket, contact }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const ticketId = ticket?.id;

  const [cards, setCards] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchCards = useCallback(async () => {
    if (!ticketId) return;
    try {
      const { data } = await api.get(`/tickets/${ticketId}/trello-cards`);
      setCards(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    if (!socket?.on) return undefined;
    const channel = `company-${user.companyId}-trellocard`;
    const onEvent = () => fetchCards();
    socket.on(channel, onEvent);
    return () => socket.off(channel, onEvent);
  }, [socket, user.companyId, fetchCards]);

  const defaultTitle =
    contact?.name && ticket?.lastMessage
      ? `${contact.name} — ${ticket.lastMessage.slice(0, 60)}`
      : contact?.name || "";

  return (
    <>
      <div className={classes.header}>
        <DashboardIcon fontSize="small" />
        <Typography variant="subtitle1">
          {i18n.t("trelloCards.widget.title")}
        </Typography>
      </div>

      {cards.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {i18n.t("trelloCards.widget.none")}
        </Typography>
      ) : (
        cards.map((c) => (
          <div key={c.id} className={classes.cardRow}>
            <Chip
              size="small"
              label={i18n.t(`trelloCards.statuses.${c.status}`)}
              style={{
                backgroundColor: STATUS_COLORS[c.status] || "#757575",
                color: "#fff"
              }}
            />
            <Typography
              variant="body2"
              style={{ flex: 1, marginLeft: 8 }}
              noWrap
            >
              {c.title}
            </Typography>
            {c.trelloCardUrl && (
              <Link
                href={c.trelloCardUrl}
                target="_blank"
                rel="noopener"
                style={{ display: "flex", alignItems: "center" }}
              >
                <OpenInNewIcon fontSize="small" />
              </Link>
            )}
          </div>
        ))
      )}

      <div className={classes.actions}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
        >
          {i18n.t("trelloCards.widget.create")}
        </Button>
      </div>

      <CreateTrelloCardModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        reload={fetchCards}
        ticketId={ticketId}
        defaults={{ title: defaultTitle }}
      />
    </>
  );
};

export default TrelloCardsWidget;
